<template>
  <v-container fluid>
    <ActionBar
      :filters="filters"
      :items="items"
      :selectedItems="selectedItems"
      @clearSelectedItems="handleClearSelectedItems"
      @change="updatePage"
      @deleteItems="openMultipleDeleteDialog"
      :noMainMenuFromV4="noMainMenuFromV4"
    />

    <div
      :class="[`${
        isFailed || !items.length
          ? 'page__content--no-items'
          : 'page__content'
      }`, {
        'page__content--from-v4': noMainMenuFromV4
      }]"
    >
      <Filters
        :status="currentStatus"
        @change="updateFilters"
      />

      <Table
        :items="items"
        :pagination="pagination"
        :status="currentStatus"
        :clearSelectedItems="clearSelectedItems"
        :drawerVisibility="drawerVisibility"
        @reloadData="reloadData"
        @goToPage="updatePage"
        @selectedItems="updateSelectedItems"
        @deleteItem="openSingleDeleteDialog"
      />
    </div>

    <SingleDeleteDialog
      :visible="singleDeleteDialog"
      :item="selectedItem"
      @close="closeSingleDeleteDialog"
    />

    <MultipleDeleteDialog
      :visible="multipleDeleteDialog"
      :items="selectedItems"
      @close="closeMultipleDeleteDialog"
    />
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import ActionBar from 'components/costCenters/ActionBar'
import Filters from 'components/costCenters/Filters'
import Table from 'components/costCenters/Table'
import SingleDeleteDialog from 'components/costCenters/dialogs/SingleDeleteDialog'
import MultipleDeleteDialog from 'components/costCenters/dialogs/MultipleDeleteDialog'

export default {
  name: 'CostCentersPage',
  components: {
    ActionBar,
    Filters,
    Table,
    SingleDeleteDialog,
    MultipleDeleteDialog
  },
  computed: {
    ...mapState({
      mainMenuIsOpen: ({ mainMenu }) => mainMenu.isOpen,
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims,
      noMainMenuFromV4: ({ mainMenu }) => mainMenu.noMainMenuFromV4
    }),

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.CostCentersPage',
      pagination: {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      },
      filters: {
        costCenterId: null,
        search: null
      },
      currentStatus: null,
      items: [],
      selectedItem: null,
      selectedItems: [],
      clearSelectedItems: false,
      singleDeleteDialog: false,
      multipleDeleteDialog: false,
      drawerVisibility: true
    }
  },

  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    updatePagination (response, itemsPerPage) {
      const totalPagesCalc = Math.ceil(response.length / itemsPerPage)
      const totalPages = totalPagesCalc > 0 ? totalPagesCalc : 1

      this.pagination = {
        ...this.pagination,
        totalElements: response.length,
        totalPages
      }
    },

    resetPagination () {
      this.pagination = {
        totalElements: 0,
        totalPages: 0,
        currentPage: 1,
        itemsPerPage: 25
      }
    },

    updateFilters (filters) {
      this.filters = { ...filters }
      this.reloadData()
    },

    updatePage (page) {
      scrollTo(0, 0)

      this.pagination = {
        ...this.pagination,
        currentPage: page
      }

      this.loadData()
    },

    updateSelectedItems (items) {
      this.selectedItems = items
    },

    handleClearSelectedItems () {
      this.clearSelectedItems = !this.clearSelectedItems
    },

    openSingleDeleteDialog (item) {
      this.singleDeleteDialog = true
      this.selectedItem = item
    },

    closeSingleDeleteDialog (reload) {
      this.singleDeleteDialog = false
      this.selectedItem = null

      if (reload) {
        this.loadData()
        this.drawerVisibility = !this.drawerVisibility
        return
      }

      this.handleClearSelectedItems()
    },

    openMultipleDeleteDialog (items) {
      this.multipleDeleteDialog = true
      this.selectedItems = items
    },

    closeMultipleDeleteDialog (reload) {
      this.multipleDeleteDialog = false
      this.selectedItems = []
      reload ? this.loadData() : this.handleClearSelectedItems()
    },

    async loadData () {
      this.currentStatus = STATUS_IS_LOADING

      try {
        const { currentPage, itemsPerPage } = this.pagination
        const company = this.selectedCompany ? this.selectedCompany.id : null
        const { costCenterId, search } = this.filters
        const { data: res } = await this.$apiService.getCostCentersAll(
          { page: currentPage },
          { limit: itemsPerPage },
          { company },
          { costCenterId },
          { search }
        )

        this.items = res.data
        this.updatePagination(res, itemsPerPage)
        this.handleClearSelectedItems()
        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR:', err)
        this.currentStatus = STATUS_FAILED
      }
    },

    reloadData () {
      this.resetPagination()
      this.loadData()
      this.handleClearSelectedItems()
    }
  },

  watch: {
    selectedCompany () {
      this.reloadData()
    },

    filters: {
      deep: true,
      handler () {
        this.drawerVisibility = !this.drawerVisibility
      }
    }
  },

  mounted () {
    this.loadData()
  }
}
</script>
